import * as React from "react";
import { EditableTable } from "../../../../../../components/Tables/NewEditableTable";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useMIS } from "../MISContext";
import { upsertPlantMis, getPlantMis } from "../../../../../../api/misApi";
import { useParams } from "react-router-dom";

const border = "1px solid var(--pv-table-border)";
const headerBg = "var(--pv-table-header-bg)";
const fontSize = "14px";
const cellPadding = "12px";

export default function InvoicesTab() {
  const { plantId } = useParams();
  const { invoicesRows, setInvoicesRows, totalAvgProgress } = useMIS();
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [editFormData, setEditFormData] = React.useState({
    progress: "",
    weightedCost: "",
    invoices: [], // Array of { date: "", amount: "" }
  });
  const [projectCost, setProjectCost] = React.useState("");
  const [paymentTerms, setPaymentTerms] = React.useState([
    "Net 30 days from invoice date",
  ]);
  const [newTerm, setNewTerm] = React.useState("");
  const [editingTermIndex, setEditingTermIndex] = React.useState(null);
  const [editingTermValue, setEditingTermValue] = React.useState("");

  const computeRowTotalAmount = React.useCallback((row) => {
    if (!row) return 0;
    return Object.keys(row).reduce((sum, key) => {
      const match = key.match(/^inv(\d+)Amount$/);
      if (!match) return sum;
      const value = Number(row[key]) || 0;
      return sum + value;
    }, 0);
  }, []);

  const computeRowWeightedCost = React.useCallback(
    (row) => {
      if (!row) return 0;
      const cost = Number(projectCost) || 0;
      const progress = Number(row.progress) || 0;
      // Treat progress as percentage of project cost
      return (cost * progress) / 100;
    },
    [projectCost],
  );

  // Load project cost and payment terms from MIS data
  React.useEffect(() => {
    const loadMisData = async () => {
      if (!plantId) return;
      try {
        const misData = await getPlantMis(plantId);

        // Log the retrieved JSON from API
        console.log("=== Retrieved MIS Data from API ===");
        console.log("Full MIS Data:", JSON.stringify(misData, null, 2));

        // The API returns data nested in data.data.invoices
        const invoicesData = misData?.data?.data?.invoices;
        console.log("Invoices Object:", invoicesData);
        console.log("Project Cost:", invoicesData?.projectCost);
        console.log("Payment Terms:", invoicesData?.paymentTerms);
        console.log("===================================");

        if (invoicesData) {
          if (invoicesData.projectCost !== undefined) {
            setProjectCost(invoicesData.projectCost);
          }
          if (
            invoicesData.paymentTerms &&
            Array.isArray(invoicesData.paymentTerms)
          ) {
            setPaymentTerms(invoicesData.paymentTerms);
          }
        }
      } catch (error) {
        console.error("Failed to load MIS data:", error);
      }
    };
    loadMisData();
  }, [plantId]);

  // Determine how many invoice columns currently exist based on keys in rows
  const invoiceCount = React.useMemo(() => {
    if (!invoicesRows || !invoicesRows.length) return 0;
    let max = 0;
    invoicesRows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const match = key.match(/^inv(\d+)(?:Date|Amount)$/);
        if (match) {
          const n = Number(match[1]);
          if (!Number.isNaN(n)) {
            max = Math.max(max, n);
          }
        }
      });
    });
    return max;
  }, [invoicesRows]);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "task",
        header: "Invoice",
        size: 180,
        enableEditing: false, // Task names are fixed categories
        muiTableHeadCellProps: {
          sx: { border, backgroundColor: headerBg, fontSize: "0.95rem", fontWeight: "bold" },
        },
        muiTableBodyCellProps: {
          sx: {
            borderLeft: border,
            borderRight: border,
            fontSize,
            padding: cellPadding,
          },
        },
      },
      {
        accessorKey: "progress",
        header: "%",
        size: 90,
        enableEditing: true,
        muiEditTextFieldProps: {
          type: "number",
          inputProps: { min: 0, max: 100 },
        },
        muiTableHeadCellProps: {
          sx: { border, backgroundColor: headerBg, fontSize: "0.95rem", fontWeight: "bold" },
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: { borderLeft: border, borderRight: border },
        },
      },
      {
        accessorKey: "weightedCost",
        header: "Weighted Cost",
        size: 110,
        enableEditing: false,
        accessorFn: (row) => computeRowWeightedCost(row),
        muiTableHeadCellProps: {
          sx: { border, backgroundColor: headerBg, fontSize: "0.95rem", fontWeight: "bold" },
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: { borderLeft: border, borderRight: border },
        },
      },
    ];

    const invoiceColumns = Array.from({ length: invoiceCount }, (_, i) => {
      const idx = i + 1;
      return {
        header: `Inv-${idx}`,
        muiTableHeadCellProps: {
          align: "center",
          sx: { border, backgroundColor: headerBg, fontSize: "0.95rem", fontWeight: "bold", pt: 1 },
        },
        columns: [
          {
            accessorKey: `inv${idx}Date`,
            header: "Date",
            size: 90,
            enableEditing: false,
            muiTableHeadCellProps: {
              align: "center",
              sx: {
                borderLeft: border,
                backgroundColor: headerBg,
                fontSize: "0.95rem",
                fontWeight: "bold",
                borderRight: "1px solid var(--pv-table-border)",
              },
            },
            muiTableBodyCellProps: {
              align: "center",
              sx: {
                borderLeft: border,
                borderRight: "1px solid var(--pv-table-border)",
              },
            },
          },
          {
            accessorKey: `inv${idx}Amount`,
            header: "Amount",
            size: 90,
            enableEditing: false,
            muiTableHeadCellProps: {
              align: "center",
              sx: {
                borderRight: border,
                backgroundColor: headerBg,
                fontSize: "0.95rem",
                fontWeight: "bold",
              },
            },
            muiTableBodyCellProps: {
              align: "center",
              sx: {
                borderRight: border,
              },
            },
          },
        ],
      };
    });

    const totalAmountColumn = {
      id: "totalAmount",
      header: "Total Amount Invoiced",
      size: 110,
      accessorFn: (row) => computeRowTotalAmount(row),
      enableEditing: false, // Make total amount non-editable (calculated field)
      muiTableHeadCellProps: {
        sx: { border, backgroundColor: headerBg, fontSize: "0.95rem", fontWeight: "bold" },
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: { borderLeft: border, borderRight: border },
      },
    };

    const actionsColumn = {
      id: "actions",
      header: "Actions",
      size: 100,
      enableEditing: false,
      Cell: ({ row }) => {
        // Don't show edit button for summary row
        if (row.original.id === "summary-total") return null;
        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleOpenEditModal(row.original)}
          >
            Edit
          </Button>
        );
      },
      muiTableHeadCellProps: {
        sx: { border, backgroundColor: headerBg, fontSize: "0.95rem", fontWeight: "bold" },
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: { borderLeft: border, borderRight: border },
      },
    };

    // Actions column at the end
    return [
      ...baseColumns,
      ...invoiceColumns,
      totalAmountColumn,
      actionsColumn,
    ];
  }, [invoiceCount, computeRowTotalAmount, computeRowWeightedCost]);

  const saveInvoicesToApi = React.useCallback(
    async (updatedRows) => {
      if (!plantId) return;

      const invoicesPayload = {};

      // Dynamically find the max invoice number in the current rows
      let maxInvoiceNum = 0;
      updatedRows.forEach((row) => {
        Object.keys(row).forEach((key) => {
          const match = key.match(/^inv(\d+)(?:Date|Amount)$/);
          if (match) {
            const n = Number(match[1]);
            if (!Number.isNaN(n)) {
              maxInvoiceNum = Math.max(maxInvoiceNum, n);
            }
          }
        });
      });

      updatedRows.forEach((row) => {
        const taskKey = row.task ? row.task.toLowerCase() : null;
        if (!taskKey) return;

        const numericProgress = Number(row.progress) || 0;
        const numericProjectCost = Number(projectCost) || 0;

        const taskData = {
          progress: numericProgress,
          weightedCost: (numericProjectCost * numericProgress) / 100,
        };

        // Include all invoices up to maxInvoiceNum (if any exist)
        for (let n = 1; n <= maxInvoiceNum; n += 1) {
          const dateKey = `inv${n}Date`;
          const amountKey = `inv${n}Amount`;
          const date = row[dateKey];
          const amount = row[amountKey];

          // Include invoice object if either date or amount exists
          if (date !== undefined || amount !== undefined) {
            taskData[`inv${n}`] = {
              date: date ?? "",
              amount: amount ?? "",
            };
          }
        }

        invoicesPayload[taskKey] = taskData;
      });

      // Add project cost and payment terms to the invoices payload
      invoicesPayload.projectCost =
        projectCost !== "" ? Number(projectCost) : 0;
      invoicesPayload.paymentTerms = paymentTerms;

      const updatedMis = { invoices: invoicesPayload };

      console.log("=== Saving MIS Data to API ===");
      console.log("Plant ID:", plantId);
      console.log("Full Payload:", JSON.stringify(updatedMis, null, 2));
      console.log("Project Cost:", invoicesPayload.projectCost);
      console.log("Payment Terms:", invoicesPayload.paymentTerms);
      console.log("==============================");

      await upsertPlantMis(plantId, updatedMis);
    },
    [plantId, projectCost, paymentTerms],
  );

  const hooks = React.useMemo(
    () => ({
      useCreateItem: () => ({ mutateAsync: async () => {}, isPending: false }),
      useGetItems: () => ({
        data: invoicesRows,
        isError: false,
        isFetching: false,
        isLoading: false,
      }),
      useUpdateItem: () => ({
        mutateAsync: async ({ values }) => {
          setInvoicesRows((prev) =>
            prev.map((row) =>
              row.id === values.id
                ? {
                    ...row,
                    ...values,
                  }
                : row,
            ),
          );
        },
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
    }),
    [invoicesRows, setInvoicesRows],
  );

  const handleOpenEditModal = (row) => {
    setEditingRow(row);

    // Extract invoice data from row into array format
    const invoices = [];
    let n = 1;
    while (
      row[`inv${n}Date`] !== undefined ||
      row[`inv${n}Amount`] !== undefined ||
      row[`inv${n}Invoice-number`] !== undefined
    ) {
      invoices.push({
        date: row[`inv${n}Date`] || "",
        amount: row[`inv${n}Amount`] || "",
        invoiceNumber: row[`inv${n}Invoice-number`] || "",
      });
      n++;
    }

    setEditFormData({
      progress: row.progress ?? "",
      weightedCost: computeRowWeightedCost(row).toFixed(2),
      invoices: invoices,
    });
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingRow(null);
    setEditFormData({ progress: "", weightedCost: "", invoices: [] });
  };

  const handleAddInvoiceInModal = () => {
    setEditFormData((prev) => ({
      ...prev,
      invoices: [...prev.invoices, { date: "", amount: "", invoiceNumber: "" }],
    }));
  };

  const handleRemoveInvoiceInModal = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      invoices: prev.invoices.filter((_, i) => i !== index),
    }));
  };

  const handleInvoiceChange = (index, field, value) => {
    console.log("edit form data", editFormData);
    setEditFormData((prev) => ({
      ...prev,
      invoices: prev.invoices.map((inv, i) =>
        i === index ? { ...inv, [field]: value } : inv,
      ),
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;

    // Build updated row with new data
    const updatedRow = {
      ...editingRow,
      progress: Number(editFormData.progress) || 0,
    };

    // Clear old invoice data and add new ones
    Object.keys(updatedRow).forEach((key) => {
      if (key.match(/^inv\d+(Date|Amount)$/)) {
        delete updatedRow[key];
      }
    });

    // Add invoice data from form
    editFormData.invoices.forEach((inv, index) => {
      const n = index + 1;
      updatedRow[`inv${n}Date`] = inv.date;
      updatedRow[`inv${n}Amount`] = inv.amount;
      updatedRow[`inv${n}Invoice-number`] = inv.invoiceNumber;
    });

    // Update rows
    const updatedRows = invoicesRows.map((row) =>
      row.id === editingRow.id ? updatedRow : row,
    );
    setInvoicesRows(updatedRows);

    // Save to API
    await saveInvoicesToApi(updatedRows);

    handleCloseEditModal();
  };

  const handleSave = async () => {
    // Manual save using the same helper
    await saveInvoicesToApi(invoicesRows);
  };

  const handleAddTerm = async () => {
    if (newTerm.trim()) {
      const updatedTerms = [...paymentTerms, newTerm.trim()];
      setPaymentTerms(updatedTerms);
      setNewTerm("");
      // Auto-save after adding term
      await saveInvoicesToApi(invoicesRows);
    }
  };

  const handleDeleteTerm = async (index) => {
    const updatedTerms = paymentTerms.filter((_, i) => i !== index);
    setPaymentTerms(updatedTerms);
    // Auto-save after deleting term
    await saveInvoicesToApi(invoicesRows);
  };

  const handleStartEditTerm = (index) => {
    setEditingTermIndex(index);
    setEditingTermValue(paymentTerms[index]);
  };

  const handleSaveEditTerm = async () => {
    if (editingTermIndex !== null && editingTermValue.trim()) {
      const updatedTerms = paymentTerms.map((term, i) =>
        i === editingTermIndex ? editingTermValue.trim() : term,
      );
      setPaymentTerms(updatedTerms);
      setEditingTermIndex(null);
      setEditingTermValue("");
      // Auto-save after editing term
      await saveInvoicesToApi(invoicesRows);
    }
  };

  const handleCancelEditTerm = () => {
    setEditingTermIndex(null);
    setEditingTermValue("");
  };

  // Summary row showing averages and totals
  const summaryRow = React.useMemo(() => {
    if (!invoicesRows || invoicesRows.length === 0) {
      return {
        id: "summary-total",
        task: "Total",
        progress: 0,
        weightedCost: 0,
        totalAmount: 0,
      };
    }

    // Calculate total progress
    const totalProgress = invoicesRows.reduce(
      (sum, row) => sum + (Number(row.progress) || 0),
      0,
    );

    // Calculate total weighted cost
    const totalWeightedCost = invoicesRows.reduce(
      (sum, row) => sum + computeRowWeightedCost(row),
      0,
    );

    // Calculate grand total amount
    const grandTotal = invoicesRows.reduce(
      (sum, row) => sum + computeRowTotalAmount(row),
      0,
    );

    // Build summary row with base fields
    const summary = {
      id: "summary-total",
      task: "Total",
      progress: totalProgress.toFixed(1),
      weightedCost: totalWeightedCost.toFixed(2),
      totalAmount: grandTotal.toFixed(2),
    };

    // Add totals for each invoice amount column
    for (let n = 1; n <= invoiceCount; n += 1) {
      const dateKey = `inv${n}Date`;
      const amountKey = `inv${n}Amount`;

      // Leave date empty for summary
      summary[dateKey] = "";

      // Calculate total for this invoice amount
      summary[amountKey] = invoicesRows
        .reduce((sum, row) => sum + (Number(row[amountKey]) || 0), 0)
        .toFixed(2);
    }

    return summary;
  }, [invoicesRows, invoiceCount, computeRowTotalAmount]);

  const tableData = React.useMemo(
    () => [...invoicesRows, summaryRow],
    [invoicesRows, summaryRow],
  );

  return (
    <Box width="100%">
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            border: "2px solid",
            borderColor: "primary.main",
            borderRadius: 2,
            px: 3,
            py: 3,
            minWidth: 450,
            backgroundColor: "background.paper",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 700,
              fontSize: "1.25rem",
              color: "primary.main",
            }}
          >
            Project Information
          </Typography>

          {/* Project Cost and Total Progress */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              gap: 4,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontSize: "1rem" }}
              >
                Project Cost:
              </Typography>
              <TextField
                type="number"
                value={projectCost}
                onChange={(e) => setProjectCost(e.target.value)}
                onBlur={() => saveInvoicesToApi(invoicesRows)}
                size="medium"
                sx={{
                  width: 140,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1rem",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                      $
                    </Typography>
                  ),
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontSize: "1rem" }}
              >
                Total Progress:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: "primary.main",
                  minWidth: 50,
                }}
              >
                {/* {invoicesRows && invoicesRows.length > 0
                  ? (
                      invoicesRows.reduce(
                        (sum, row) => sum + (Number(row.progress) || 0),
                        0,
                      ) / invoicesRows.length
                    ).toFixed(1)
                  : "0.0"} */}
                {totalAvgProgress}%
              </Typography>
            </Box>
          </Box>

          {/* Payment Terms */}
          {/* <Box> */}
          {/* <Typography 
              variant="body1" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Payment Terms:
            </Typography> */}
          {/* <Box 
              component="ol" 
              sx={{ 
                margin: 0, 
                paddingLeft: "2rem", 
                mb: 2,
                "& li": {
                  fontSize: "1rem",
                  lineHeight: 1.8,
                },
              }}
            >
              {paymentTerms.map((term, index) => (
                <Box
                  component="li"
                  key={index}
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    padding: "8px 12px",
                    borderRadius: 1,
                    backgroundColor: index % 2 === 0 ? "action.hover" : "transparent",
                  }}
                >
                  {editingTermIndex === index ? (
                    <>
                      <TextField
                        value={editingTermValue}
                        onChange={(e) => setEditingTermValue(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            handleSaveEditTerm();
                          }
                        }}
                        size="medium"
                        autoFocus
                        sx={{ 
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            fontSize: "1rem",
                          },
                        }}
                      />
                      <Button
                        size="medium"
                        onClick={handleSaveEditTerm}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        size="medium"
                        onClick={handleCancelEditTerm}
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ flex: 1, fontSize: "1rem", fontWeight: 400 }}>
                        {term}
                      </Typography>
                      <Button
                        size="medium"
                        onClick={() => handleStartEditTerm(index)}
                        variant="text"
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="medium"
                        onClick={() => handleDeleteTerm(index)}
                        variant="text"
                        color="error"
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Box>
              ))}
            </Box> */}

          {/* Add New Term */}
          {/* <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                placeholder="Add new term..."
                value={newTerm}
                onChange={(e) => setNewTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddTerm();
                  }
                }}
                size="medium"
                sx={{ 
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "1rem",
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleAddTerm}
                disabled={!newTerm.trim()}
                sx={{ fontSize: "0.95rem", fontWeight: 600 }}
              >
                Add Term
              </Button>
            </Box> */}
          {/* </Box> */}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save All
          </Button>
        </Box>
      </Box>
      <EditableTable
        name="Invoices"
        columns={columns}
        hooks={hooks}
        editMode="cell"
        enableCreate={false}
        enableEditing={false}
        enableDelete={false}
        data={tableData}
      />

      {/* Edit Row Modal */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit {editingRow?.task || "Task"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
            {/* Progress Field */}
            <TextField
              label="Progress (%)"
              type="number"
              value={editFormData.progress}
              onChange={(e) => {
                const value = e.target.value;
                const numericProgress = Number(value) || 0;
                const numericCost = Number(projectCost) || 0;
                const computedWeighted = (numericCost * numericProgress) / 100;
                setEditFormData((prev) => ({
                  ...prev,
                  progress: value,
                  weightedCost: computedWeighted.toFixed(2),
                }));
              }}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />

            {/* Weighted Cost Field (read-only, derived) */}
            <TextField
              label="Weighted Cost"
              type="number"
              value={editFormData.weightedCost}
              InputProps={{ readOnly: true }}
              fullWidth
            />

            {/* Invoices Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Invoices
              </Typography>
              {editFormData.invoices.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  No invoices added yet
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {editFormData.invoices.map((invoice, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography sx={{ minWidth: 60 }}>
                        Inv-{index + 1}
                      </Typography>
                      <TextField
                        label="Date"
                        type="date"
                        value={invoice.date}
                        onChange={(e) =>
                          handleInvoiceChange(index, "date", e.target.value)
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Amount"
                        type="number"
                        value={invoice.amount}
                        onChange={(e) =>
                          handleInvoiceChange(index, "amount", e.target.value)
                        }
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Invoice-number"
                        type="text"
                        value={invoice.invoiceNumber}
                        onChange={(e) =>
                          handleInvoiceChange(
                            index,
                            "invoiceNumber",
                            e.target.value,
                          )
                        }
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveInvoiceInModal(index)}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
              <Button
                variant="outlined"
                onClick={handleAddInvoiceInModal}
                sx={{ mt: 2 }}
              >
                + Add Invoice
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
